import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { RootState } from "../../../redux/store";
import logo from "../../../assets/cosmoola/logo.jpg";
import { ArrowRight, Shield, Zap, Globe, Code, ChevronRight } from "lucide-react";

/* ─── THEME (matches wallet.tsx) ─── */
const D = { bg:"#000000", surface:"#0a0a0a", surface2:"#101010", border:"#1c1c1c", border2:"#2a2a2a", white:"#ffffff", offwhite:"#e8e8e8", muted:"#6b6b6b", subtle:"#3a3a3a", gold:"#C9A96E" };

const GOLD_GRAD = "linear-gradient(135deg, #E8C96A, #C9A84C, #9A7A2E)";

const FEATURES = [
  { num:"01", title:"Accept Payments", sub:"Globally", desc:"Cards, mobile money, bank transfers — unified under one elegant API. 40+ African & European markets.", icon: Globe },
  { num:"02", title:"Instant", sub:"Settlements", desc:"Real-time settlement to your bank account. No waiting days. Your money, your terms.", icon: Zap },
  { num:"03", title:"Developer", sub:"First", desc:"Beautiful SDKs, comprehensive webhooks, documentation that reads like poetry. Minutes, not months.", icon: Code },
  { num:"04", title:"Enterprise", sub:"Security", desc:"PCI DSS Level 1. End-to-end encryption. ML-powered fraud detection. Your trust, protected.", icon: Shield },
];

const MARQUEE = ["ACCEPT PAYMENTS","◆","MOBILE MONEY","◆","CARD PROCESSING","◆","INSTANT SETTLEMENTS","◆","40+ MARKETS","◆","ONE API","◆","SECURE","◆","ELEGANT","◆","POWERFUL","◆"];

export default function MerchantOverview() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const user = useSelector((s: RootState) => s.auth.user);

  useEffect(() => {
    const fn = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const navOp = Math.min(scrollY / 200, 1);

  const handleStart = () => {
    if (user?.userId) navigate(`/merchantonboarding/${user.userId}`);
    else navigate("/login");
  };


    const handleMerchant = () => {
    if (user?.userId) navigate(`/merchantdashboard/${user.userId}`);
    else navigate("/login");
  };


  return (
    <div style={{ background: D.bg, color: D.white, minHeight: "100vh", fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{CSS}</style>

      {/* ═══ NAV ═══ */}
      <nav style={{ position:"fixed", top:0, left:0, right:0, zIndex:1000, background:`rgba(0,0,0,${0.5+navOp*0.45})`, backdropFilter: navOp>0.1?"blur(16px)":"none", borderBottom:`1px solid rgba(255,255,255,${navOp*0.06})`, transition:"all .4s" }}>
        <div style={{ maxWidth:1400, margin:"0 auto", padding:"0 24px", display:"flex", alignItems:"center", justifyContent:"space-between", height:58 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, cursor:"pointer" }} onClick={() => window.scrollTo({top:0,behavior:"smooth"})}>
            <img src={logo} alt="C" style={{ width:28, height:28, objectFit:"cover" }} />
            <span style={{ color:D.white, fontWeight:300, letterSpacing:"0.35em", fontFamily:"'Cormorant Garamond',serif", fontSize:15 }}>COSMOOLA</span>
          </div>
          <div className="mo-nav-links">
            {["Products","Developers","Pricing","Company"].map(item => (
              <a key={item} href={`#${item.toLowerCase()}`} style={{ color:D.muted, fontSize:10, letterSpacing:"0.15em", textTransform:"uppercase", textDecoration:"none", fontWeight:500, transition:"color .3s" }}
                onMouseEnter={e=>(e.currentTarget.style.color=D.white)} onMouseLeave={e=>(e.currentTarget.style.color=D.muted)}>{item}</a>
            ))}
            <button onClick={handleMerchant} style={{ padding:"10px 28px", background:"transparent", border:`1px solid ${D.border2}`, color:D.white, fontSize:9, letterSpacing:"0.18em", textTransform:"uppercase", fontWeight:600, cursor:"pointer", transition:"all .3s" }}
              onMouseEnter={e=>{e.currentTarget.style.background=D.white;e.currentTarget.style.color=D.bg}} onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=D.white}}>Already a Merchant?</button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section style={{ minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"center", position:"relative", padding:"140px 24px 100px" }}>
        <div style={{ maxWidth:1400, margin:"0 auto", width:"100%" }}>
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.2}}>
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:40 }}>
              <div style={{ width:40, height:1, background:D.gold }} />
              <span style={{ fontSize:9, letterSpacing:"0.3em", color:D.gold, fontWeight:600, textTransform:"uppercase" }}>The Future of Payments</span>
            </div>
          </motion.div>

          <motion.h1 initial={{opacity:0,y:40}} animate={{opacity:1,y:0}} transition={{delay:0.4,duration:1}}
            style={{ fontSize:"clamp(42px,7vw,100px)", fontFamily:"'Cormorant Garamond',serif", fontWeight:300, lineHeight:1.0, letterSpacing:"-0.02em", marginBottom:36, maxWidth:800 }}>
            <span style={{ display:"block", fontStyle:"italic", color:D.offwhite }}>Redefining</span>
            <span style={{ display:"block", fontWeight:600 }}>Digital</span>
            <span style={{ display:"block", fontWeight:600, background:GOLD_GRAD, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Payments</span>
          </motion.h1>

          <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.8}}
            style={{ fontSize:15, fontFamily:"'Cormorant Garamond',serif", color:D.muted, lineHeight:1.8, maxWidth:480, fontWeight:400, letterSpacing:"0.02em" }}>
            A payment infrastructure crafted for the modern era. Accept payments across Africa and Europe with <span style={{ color:D.white, fontStyle:"italic" }}>unmatched elegance</span>.
          </motion.p>

          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:1.1}} style={{ display:"flex", gap:16, marginTop:48, flexWrap:"wrap" }}>
            <button onClick={handleStart} className="mo-cta-primary">
              START NOW <ArrowRight size={14} style={{marginLeft:8}} />
            </button>
            <button onClick={() => navigate("/contact")} className="mo-cta-secondary">CONTACT SALES</button>
          </motion.div>

          <motion.div initial={{opacity:0}} animate={{opacity:1}} transition={{delay:1.5}}
            style={{ display:"flex", gap:48, marginTop:80, paddingTop:40, borderTop:`1px solid ${D.border}`, flexWrap:"wrap" }}>
            {[{val:"$2.4B+",lbl:"Processed"},{val:"40+",lbl:"Markets"},{val:"99.99%",lbl:"Uptime"},{val:"<50ms",lbl:"Latency"}].map((s,i) => (
              <div key={i}>
                <div style={{ fontSize:24, fontFamily:"'Cormorant Garamond',serif", fontWeight:600, color:D.white }}>{s.val}</div>
                <div style={{ fontSize:9, letterSpacing:"0.15em", color:D.muted, marginTop:4, textTransform:"uppercase" }}>{s.lbl}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ MARQUEE ═══ */}
      <section style={{ borderTop:`1px solid ${D.border}`, borderBottom:`1px solid ${D.border}`, padding:"20px 0", overflow:"hidden" }}>
        <div style={{ display:"flex", animation:"marqueeScroll 30s linear infinite", width:"max-content" }}>
          {[...MARQUEE,...MARQUEE].map((item,i) => (
            <span key={i} style={{ fontSize:10, letterSpacing:"0.2em", color:item==="◆"?D.gold:D.muted, fontWeight:500, padding:"0 20px", whiteSpace:"nowrap" }}>{item}</span>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="products" style={{ padding:"120px 24px", maxWidth:1400, margin:"0 auto" }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
          <div style={{ width:40, height:1, background:D.gold }} />
          <span style={{ fontSize:9, letterSpacing:"0.3em", color:D.gold, fontWeight:600, textTransform:"uppercase" }}>Capabilities</span>
        </div>
        <h2 style={{ fontSize:"clamp(32px,4vw,56px)", fontFamily:"'Cormorant Garamond',serif", fontWeight:300, lineHeight:1.1, marginBottom:60, maxWidth:500 }}>
          <span style={{ fontStyle:"italic", color:D.offwhite }}>Built for</span><br /><span style={{ fontWeight:600 }}>Ambition</span>
        </h2>

        <div className="mo-features-grid">
          {FEATURES.map((f,i) => {
            const Icon = f.icon;
            return (
              <div key={i} onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)}
                style={{ background:hovered===i?D.surface2:D.surface, padding:"40px 28px", border:`1px solid ${D.border}`, position:"relative", overflow:"hidden", transition:"all .4s", cursor:"default" }}>
                <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:GOLD_GRAD, transform:hovered===i?"scaleX(1)":"scaleX(0)", transformOrigin:"left", transition:"transform .5s" }} />
                <Icon size={20} color={D.gold} strokeWidth={1.5} style={{ marginBottom:20 }} />
                <div style={{ fontSize:11, color:D.gold, letterSpacing:"0.1em", marginBottom:16 }}>{f.num}</div>
                <div style={{ fontSize:18, fontFamily:"'Cormorant Garamond',serif", fontWeight:600, marginBottom:4 }}>{f.title}</div>
                <div style={{ fontSize:18, fontFamily:"'Cormorant Garamond',serif", fontWeight:300, fontStyle:"italic", color:D.offwhite, marginBottom:20 }}>{f.sub}</div>
                <p style={{ fontSize:12, color:D.muted, lineHeight:1.8 }}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ CTA SPLIT ═══ */}
      <section style={{ padding:"0 24px 120px", maxWidth:1400, margin:"0 auto" }}>
        <div className="mo-split">
          <div style={{ background:D.surface, border:`1px solid ${D.border}`, padding:"80px 48px", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", inset:0, opacity:0.02, backgroundImage:`repeating-linear-gradient(45deg,${D.white} 0,${D.white} 1px,transparent 0,transparent 50%)`, backgroundSize:"20px 20px" }} />
            <div style={{ position:"relative" }}>
              <div style={{ fontSize:80, fontFamily:"'Cormorant Garamond',serif", fontWeight:300, color:D.gold, lineHeight:0.85, marginBottom:28, opacity:0.6 }}>✦</div>
              <blockquote style={{ fontSize:22, fontFamily:"'Cormorant Garamond',serif", fontWeight:300, fontStyle:"italic", color:D.offwhite, lineHeight:1.6 }}>
                "The new standard of online payments for your business."
              </blockquote>
              <div style={{ width:40, height:1, background:D.gold, margin:"28px 0" }} />
              <div style={{ fontSize:9, letterSpacing:"0.2em", color:D.muted, textTransform:"uppercase" }}>Cosmoola Vision</div>
            </div>
          </div>
          <div style={{ background:D.surface, border:`1px solid ${D.border}`, padding:"80px 48px", display:"flex", flexDirection:"column", justifyContent:"center" }}>
            <div style={{ fontSize:9, letterSpacing:"0.25em", color:D.gold, fontWeight:600, marginBottom:24, textTransform:"uppercase" }}>Ready to Begin?</div>
            <h3 style={{ fontSize:32, fontFamily:"'Cormorant Garamond',serif", fontWeight:600, lineHeight:1.2, marginBottom:20 }}>
              Start accepting<br />payments <span style={{ fontWeight:300, fontStyle:"italic", color:D.offwhite }}>today</span>
            </h3>
            <p style={{ fontSize:12, color:D.muted, lineHeight:1.8, marginBottom:40, maxWidth:340 }}>
              Join thousands of businesses across Africa and Europe who trust Cosmoola for their payment infrastructure.
            </p>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              <button onClick={handleStart} className="mo-cta-primary" style={{ padding:"14px 36px" }}>Start Now <ChevronRight size={14} /></button>
              <button onClick={() => navigate("/contact")} className="mo-cta-secondary" style={{ padding:"14px 36px" }}>Contact Sales</button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ background:D.bg, borderTop:`1px solid ${D.border}`, padding:"28px 24px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <div style={{ fontSize:11, color:D.muted }}>© 2025 – {new Date().getFullYear()} Cosmoola Technologies</div>
          <div style={{ display:"flex", gap:20 }}>
            {["Privacy","Terms","Support"].map(l => (
              <span key={l} onClick={() => navigate(`/${l.toLowerCase()}`)} style={{ fontSize:11, color:D.muted, cursor:"pointer", textDecoration:"underline", textUnderlineOffset:3 }}>{l}</span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
*,::before,::after{box-sizing:border-box;margin:0;padding:0}
::selection{background:#C9A96E;color:#000}
@keyframes marqueeScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
::-webkit-scrollbar{width:0;height:0}

.mo-nav-links{display:flex;align-items:center;gap:36}
.mo-cta-primary{display:inline-flex;align-items:center;padding:16px 40px;background:#fff;color:#000;border:none;font-size:10px;letter-spacing:0.18em;text-transform:uppercase;font-family:'Montserrat',sans-serif;font-weight:700;cursor:pointer;transition:all .3s}
.mo-cta-primary:hover{background:#C9A96E;transform:translateY(-1px);box-shadow:0 8px 28px rgba(201,169,110,0.2)}
.mo-cta-secondary{display:inline-flex;align-items:center;padding:16px 40px;background:transparent;color:#fff;border:1px solid #2a2a2a;font-size:10px;letter-spacing:0.18em;text-transform:uppercase;font-family:'Montserrat',sans-serif;font-weight:500;cursor:pointer;transition:all .3s}
.mo-cta-secondary:hover{border-color:#C9A96E;color:#C9A96E}

.mo-features-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:1px}
.mo-split{display:grid;grid-template-columns:1fr 1fr;gap:1px}

@media(max-width:900px){
  .mo-nav-links{gap:16}
  .mo-nav-links a{display:none}
  .mo-features-grid{grid-template-columns:1fr!important}
  .mo-split{grid-template-columns:1fr!important}
}
@media(max-width:600px){
  .mo-nav-links{gap:0}
}
`;